'use client';

import { motion, useInView, useScroll, useTransform, useSpring, useMotionValue } from 'framer-motion';
import { useRef, MouseEvent } from 'react';
import Link from 'next/link';
import { ArrowRight } from 'lucide-react';

const easing = [0.22, 1, 0.36, 1] as const;

const words = ['Siamo', 'il', 'team', 'che', 'ti', 'mancava.'];

// Magnetic CTA button
function MagneticButton({ href, children }: { href: string; children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 200, damping: 20 });
  const sy = useSpring(y, { stiffness: 200, damping: 20 });

  function handleMouseMove(e: MouseEvent<HTMLDivElement>) {
    const rect = ref.current!.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    x.set((e.clientX - cx) * 0.3);
    y.set((e.clientY - cy) * 0.3);
  }

  function handleMouseLeave() {
    x.set(0);
    y.set(0);
  }

  return (
    <div ref={ref} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} className="inline-block">
      <motion.div style={{ x: sx, y: sy }}>
        <Link
          href={href}
          className="group inline-flex items-center justify-center gap-3 rounded-full bg-primary px-10 py-4 text-base font-semibold text-white shadow-lg shadow-primary/20 transition-all hover:bg-primary/90 hover:shadow-primary/40 hover:shadow-2xl active:scale-[0.98]"
        >
          {children}
          <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </motion.div>
    </div>
  );
}

export default function CtaSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-15% 0px' });

  // Parallax on background element
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start end', 'end start'] });
  const bgY = useSpring(useTransform(scrollYProgress, [0, 1], ['-10%', '10%']), { stiffness: 40, damping: 15 });

  return (
    <section
      ref={sectionRef}
      className="relative w-full min-h-[100dvh] flex items-center overflow-hidden border-t border-white/[0.06] bg-gray-950"
    >
      {/* Parallax ambient glow */}
      <motion.div className="pointer-events-none absolute inset-0" style={{ y: bgY }}>
        <div className="absolute left-1/4 top-1/3 h-[50vw] w-[50vw] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/[0.06] blur-[100px]" />
        <div className="absolute right-1/4 bottom-1/3 h-[35vw] w-[35vw] translate-x-1/2 translate-y-1/2 rounded-full bg-primary/[0.04] blur-[120px]" />
      </motion.div>

      {/* Horizontal rule decoration */}
      <motion.div
        className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent"
        initial={{ scaleX: 0 }}
        animate={isInView ? { scaleX: 1 } : {}}
        transition={{ duration: 1.4, ease: easing }}
      />

      <div ref={ref} className="relative mx-auto max-w-7xl px-6 lg:px-8 py-32 w-full">
        <div className="max-w-5xl">

          {/* Animated word-by-word headline */}
          <h2 className="text-4xl font-black tracking-tight text-white leading-[1.1] lg:text-5xl xl:text-6xl">
            {words.map((word, i) => (
              <motion.span
                key={i}
                className="inline-block mr-[0.22em]"
                initial={{ opacity: 0, y: 36, skewY: 3 }}
                animate={isInView ? { opacity: 1, y: 0, skewY: 0 } : {}}
                transition={{ duration: 0.6, delay: i * 0.07, ease: easing }}
              >
                {word === 'mancava.' ? (
                  <span className="text-primary">{word}</span>
                ) : (
                  word
                )}
              </motion.span>
            ))}
          </h2>

          <motion.p
            className="mt-8 text-lg leading-8 text-gray-400 max-w-xl"
            initial={{ opacity: 0, y: 24 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.6, ease: easing }}
          >
            Scrivi cosa ti serve. Hai una risposta vera senza attese, senza slide, senza tempo sprecato.
          </motion.p>

          <motion.div
            className="mt-12 flex flex-col gap-4 sm:flex-row sm:items-center"
            initial={{ opacity: 0, y: 24 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.75, ease: easing }}
          >
            <MagneticButton href="/contatti">Inizia adesso</MagneticButton>
            <span className="text-sm text-gray-600 sm:pl-2">
              Nessuna proposta preconfezionata. Mai.
            </span>
          </motion.div>

        </div>

        {/* Decorative large arrow */}
        <motion.div
          className="absolute bottom-8 right-8 text-[12rem] font-black leading-none text-white/[0.025] select-none pointer-events-none hidden lg:block"
          initial={{ opacity: 0, x: 40 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 1, delay: 0.5, ease: easing }}
          aria-hidden
        >
          →
        </motion.div>
      </div>
    </section>
  );
}
